import './App.css'
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import Application from './pages/Application/Application'
import { useEffect, useState } from 'react'
import { apiURL } from './utilities/URLs'
import { refreshAccessToken } from './utilities/extensions'
import { getTrackingHeaders, setStoredSessionId } from './utilities/tracking/sessionStorage'
import { account } from './classes/Account'
import Navbar from './components/Navbar'
import Account from './pages/Account'
import Verify from './pages/Login/Verify'
import Callback from './pages/Login/Callback'
import AddBankingLogin from './pages/Login/AddBankingLogin'
import Login from './pages/Login/Login'
import ImpersonationBar from './components/ImpersonationBar'
import ACHBanner from './components/ACHBanner'
import PaymentInstallmentsBanner from './components/PaymentInstallmentsBanner'
import ApplicationDashboard from './pages/Application/Dashboard/Dashboard'
import DocumentCenter from './pages/Application/DocumentCenter/DocumentCenter'
import Dashboard from './pages/Dashboard/Dashboard'
import BookMeeting from './pages/Application/BookMeeting/BookMeeting'
import NewApplication from './pages/Application/New/NewApplication'
import RefundOptions from './pages/Application/RefundOptions/RefundOptions'
import QuestionnairePage from './pages/Application/Questionnaire/QuestionnairePage'
import ERCAgreementPage from './pages/Application/ERCAgreement/ERCAgreementPage'
import PostHoldAddendumPage from './pages/Application/PostHoldAddendum/PostHoldAddendumPage'
import IDVerificationPage from './pages/Application/IDVerification/IDVerificationPage'
import ESignPage from './pages/Application/ESign/ESignPage'
import ESign8821Page from './pages/Application/ESign/ESign8821Page'
import Withdrawal8821AckPage from './pages/Application/ESign/Withdrawal8821AckPage'
import GenericESignPage from './pages/ESign/ESignPage'
import PacketPage from './pages/Application/Packet/PacketPage'
import ShippingLabelPage from './pages/Application/Packet/ShippingLabel/ShippingLabelPage'
import DocumentsPage from './pages/Application/Documents/DocumentsPage'
import QuarterBreakdownPage from './pages/Application/QuarterBreakdown/QuarterBreakdown'
import TranscriptReviewHoldPage from './pages/Application/QuarterBreakdown/TranscriptReviewHold/TranscriptReviewHoldPage'
import VerifyRefundPage from './pages/Application/QuarterBreakdown/VerifyRefund/VerifyRefundPage'
import BookCallPage from './pages/Application/QuarterBreakdown/BookCall/BookCallPage'
import BankVerificationPage from './pages/Application/BankVerification/BankVerificationPage'
import BusinessVerificationUploadPage from './pages/Application/BusinessVerificationUpload/BusinessVerificationUploadPage'
import ACHAgreementPage from './pages/Application/ACHAgreement/ACHAgreementPage'
import PaymentInstallmentsWizardPage from './pages/Application/PaymentInstallments/PaymentInstallmentsWizardPage'
import PaymentInstallmentStatusPage from './pages/Application/PaymentInstallments/PaymentInstallmentStatusPage'
import useIframeAutoResize from './hooks/useIframeAutoResize'
import RouteTracker from './utilities/tracking/RouteTracker.jsx'

import './css/loader.css'

function App() {
  const navigate = useNavigate()
  const location = useLocation()
  const [loggingIn, setLoggingIn] = useState(false)
  const [showNavBar, setShowNavBar] = useState(false)
  const [impersonationMode, setImpersonationMode] = useState('')
  const [agentId, setAgentId] = useState('')
  const [applicationId, setApplicationId] = useState('')
  const [processorId, setProcessorId] = useState('')
  const [loginMessage, setLoginMessage] = useState('Starting application...')

  useEffect(() => {
    const parts = window.location.pathname.split('/')
    const fullPath = `${window.location.pathname}${window.location.search}`
    const loginRedirect = fullPath && fullPath !== '/' ? fullPath : '/dashboard'
    let accessToken = localStorage.getItem('portalAccessToken')

    const handleLoginWithAccessToken = async () => {
      setLoggingIn(true)

      const loginResponse = await fetch(`${apiURL}/api/login/token`, {
        method: 'GET',
        headers: {
          'Authorization': accessToken,
          ...getTrackingHeaders()
        },
        credentials: 'include'
      })
      const loginData = await loginResponse.json()
      if (loginData.response.error) {
        if (loginData.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          if (refreshedToken) accessToken = refreshedToken
          refreshedToken && refreshedToken.length > 0 ? await handleLoginWithAccessToken() : navigate(`/?redirect=${encodeURIComponent(loginRedirect)}`, { state: { expired: true } })
        } else {
          console.error(loginData.response.error)
          setLoggingIn(false)
        }
      } else {
        account.setAccount(loginData.response.account)
        account.accessToken = accessToken
        if (loginData.response.sessionId)
          setStoredSessionId(loginData.response.sessionId)
        navigate((parts.length > 1 && parts[1] ? `/${parts.slice(1).join('/')}` : '/dashboard') + location.search)
        setLoggingIn(false)
        setShowNavBar(true)
      }
    }

    /**
     * @param {String} applicationId
     * @param {String} token
     */
    const handleBankingAccessLogin = async (applicationId, token) => {
      setLoggingIn(true)
      setLoginMessage('Preparing your secure banking setup...')
      setApplicationId(applicationId)

      try {
        const loginResponse = await fetch(`${apiURL}/api/login/banking/${applicationId}/${token}`, {
          credentials: 'include',
          headers: getTrackingHeaders()
        })
        const loginData = await loginResponse.json()

        if (loginData.response?.error) {
          console.error(loginData.response.error)
          setLoggingIn(false)
          setLoginMessage('Starting application...')
          const bankingRedirect = `/applications/${applicationId}/bank-verification?update=1`
          navigate(`/?bankingLinkError=${encodeURIComponent(loginData.response.error)}&redirect=${encodeURIComponent(bankingRedirect)}`, { state: { bankingLinkError: loginData.response.error } })
          return
        }

        account.setAccount(loginData.response.account)
        account.accessToken = loginData.response.accessToken
        localStorage.setItem('portalAccessToken', loginData.response.accessToken)
        if (loginData.response.sessionId)
          setStoredSessionId(loginData.response.sessionId)

        setShowNavBar(true)
        navigate(loginData.response.redirect || `/applications/${applicationId}/bank-verification?update=1`)
        setLoggingIn(false)
        setLoginMessage('Starting application...')
      } catch (error) {
        console.error(error)
        setLoggingIn(false)
        setLoginMessage('Starting application...')
        const bankingRedirect = `/applications/${applicationId}/bank-verification?update=1`
        navigate(`/?bankingLinkError=${encodeURIComponent('Something went wrong while opening your banking link. Please sign in and try again.')}&redirect=${encodeURIComponent(bankingRedirect)}`)
      }
    }

    /**
     * @param {String} applicationId 
     */
    const handleImpersonateApplication = async (applicationId) => {
      setLoggingIn(true)

      const loginResponse = await fetch(`${apiURL}/api/login/impersonate/${applicationId}`)
      const loginData = await loginResponse.json()
      if (loginData.response.error)
        console.error(loginData.response.error)
      else {
        account.setAccount(loginData.response.account)
        account.accessToken = loginData.response.accessToken
        if (loginData.response.agentId) setAgentId(loginData.response.agentId)
        navigate('/dashboard')
        setLoggingIn(false)
        setShowNavBar(true)
        setImpersonationMode(applicationId)
      }
    }

    /**
     * @param {String[]} parts 
     */
    const handleNavigation = (parts) => {
      switch (parts[1]) {
        case 'application':
          if (parts.includes('questionnaire')) {
            setLoggingIn(true)
            handleLoginWithAccessToken()
          } else {
            const path = `/${parts[1]}/${parts[1] === 'application' ? (parts[2] || 'website') : ''}`
            const searchParams = window.location.search
            navigate(`${path}${searchParams}`)
          }
          break
        case 'verify':
          navigate(`/verify${window.location.search}`)
          break
        case 'callback':
          navigate(`/callback${window.location.search}`)
          break
        case 'e-sign':
          navigate(`/e-sign${window.location.search}`)
          break
        case 'impersonate':
          setLoggingIn(true)
          handleImpersonateApplication(parts[2])
          break
        case 'banking-access':
          if (parts[2] && parts[3])
            handleBankingAccessLogin(parts[2], parts[3])
          else
            navigate('/')
          break
        case 'applications':
          if (parts[3] === 'add-banking' && parts[2] && parts[4]) {
            handleBankingAccessLogin(parts[2], parts[4])
            break
          }
          if (accessToken) {
            setLoggingIn(true)
            handleLoginWithAccessToken()
          } else {
            navigate(`/?redirect=${encodeURIComponent(loginRedirect)}`)
          }
          break
        default:
          if (accessToken) {
            setLoggingIn(true)
            handleLoginWithAccessToken()
          } else
            navigate(`/?redirect=${encodeURIComponent(loginRedirect)}`)
          break
      }
    }

    if (parts.length > 1 && parts[1])
      handleNavigation(parts)
    else {
      if (accessToken)
        handleLoginWithAccessToken()
    }
  }, [])
  const isEmbeddedESign = location.pathname === '/e-sign'
  const isDashboard = location.pathname === '/dashboard' || location.pathname === '/'
  const isPublicApplication = location.pathname.startsWith('/application/')
  const { isIframed } = useIframeAutoResize()
  const useIframeLayout = isIframed && isPublicApplication

  return (
    <>
      {!loggingIn ? (
        <div className={
          useIframeLayout
            ? 'w-full min-h-screen flex flex-col font-poppins bg-slate-50 dark:bg-slate-900 dark:text-gray-100 transition-colors duration-300'
            : 'w-screen min-h-screen h-screen flex flex-col md:flex-row font-poppins bg-slate-50 dark:bg-slate-900 dark:text-gray-100 transition-colors duration-300 overflow-hidden'
        }>
          {showNavBar && !isEmbeddedESign && !isDashboard && !useIframeLayout && (
            <Navbar applicationId={applicationId} processorId={processorId} />
          )}
          <div id='app-content' className={
            useIframeLayout
              ? 'flex-1 min-w-0 flex flex-col relative items-center'
              : `flex-1 min-w-0 h-full overflow-y-auto overflow-x-hidden flex flex-col relative ${isEmbeddedESign ? '' : 'items-center'}`
          }>
            {impersonationMode && !isEmbeddedESign && (
              <div className="w-full">
                <ImpersonationBar applicationId={impersonationMode} />
              </div>
            )}
            {showNavBar && !isEmbeddedESign && (
              <>
                <ACHBanner applicationId={applicationId} />
                <PaymentInstallmentsBanner applicationId={applicationId} />
              </>
            )}
            {showNavBar && <RouteTracker />}
            <Routes>
              <Route path='/' element={<Login />} />
              <Route path='/verify' element={<Verify setShowNavBar={setShowNavBar} />} />
              <Route path='/callback' element={<Callback setShowNavBar={setShowNavBar} />} />
              <Route path='/banking-access/:applicationId/:token' element={<AddBankingLogin setShowNavBar={setShowNavBar} setApplicationId={setApplicationId} />} />
              <Route path='/applications/:applicationId/add-banking/:token' element={<AddBankingLogin setShowNavBar={setShowNavBar} setApplicationId={setApplicationId} />} />
              <Route path='/application/:affiliateName' element={<Application />} />
              <Route path='/dashboard' element={<Dashboard impersonationMode={impersonationMode} setApplicationId={setApplicationId} setProcessorId={setProcessorId} />} />
              <Route path='/applications/new' element={<NewApplication agentId={agentId} />} />
              <Route path='/applications/:applicationId/refund-options' element={<RefundOptions />} />
              <Route path='/applications/:applicationId/questionnaire' element={<QuestionnairePage />} />
              <Route path='/applications/:applicationId/dashboard' element={<ApplicationDashboard />} />
              <Route path='/applications/:applicationId/document-center' element={<DocumentCenter />} />
              <Route path='/applications/:applicationId/book-meeting' element={<BookMeeting />} />
              <Route path='/account' element={<Account setShowNavBar={setShowNavBar} />} />
              <Route path='/applications/:applicationId/erc-agreement' element={<ERCAgreementPage />} />
              <Route path='/applications/:applicationId/post-hold-addendum' element={<PostHoldAddendumPage />} />
              <Route path='/applications/:applicationId/id-verification' element={<IDVerificationPage />} />
              <Route path='/applications/:applicationId/e-sign' element={<ESignPage />} />
              <Route path='/applications/:applicationId/e-sign/8821' element={<ESign8821Page />} />
              <Route path='/applications/:applicationId/e-sign/8821-withdrawal-ack' element={<Withdrawal8821AckPage />} />
              <Route path='/applications/:applicationId/packet' element={<PacketPage />} />
              <Route path='/applications/:applicationId/packet/shipping-label' element={<ShippingLabelPage />} />
              <Route path='/applications/:applicationId/documents' element={<DocumentsPage />} />
              <Route path='/applications/:applicationId/quarter-breakdown' element={<QuarterBreakdownPage />} />
              <Route path='/applications/:applicationId/quarter-breakdown/:quarter/transcript-review-hold' element={<TranscriptReviewHoldPage />} />
              <Route path='/applications/:applicationId/quarter-breakdown/:quarter/verify' element={<VerifyRefundPage />} />
              <Route path='/applications/:applicationId/quarter-breakdown/:quarter/book-call' element={<BookCallPage />} />
              <Route path='/applications/:applicationId/bank-verification' element={<BankVerificationPage />} />
              <Route path='/applications/:applicationId/business-verification-upload' element={<BusinessVerificationUploadPage />} />
              <Route path='/applications/:applicationId/ach-agreement' element={<ACHAgreementPage />} />
              <Route path='/applications/:applicationId/payment-installments/:planId' element={<PaymentInstallmentStatusPage />} />
              <Route path='/applications/:applicationId/payment-installments' element={<PaymentInstallmentsWizardPage />} />
              <Route path='/e-sign' element={<GenericESignPage />} />
            </Routes>
          </div>
        </div>
      ) : (
        <div className='w-screen h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-slate-900 transition-colors'>
          <div className='flex flex-col items-center gap-4 bg-white dark:bg-slate-800 p-8 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 transition-colors'>
            <Loader2 size={32} className='animate-spin text-ertc-primary' />
            <p className='text-sm font-medium text-gray-700 dark:text-slate-300 transition-colors'>{loginMessage}</p>
          </div>
        </div>
      )}
    </>
  )
}

export default App
